import { Company } from "../types";

// Re-export getServiceIcon (MUI icon) from shared Icons
export { getServiceIcon } from "../components/shared/Icons";

export interface ThemePalette {
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  secondary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  accent: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  neutral: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    black: string;
    white: string;
  };
}

// Default theme palettes by company category
const themePalettes: Record<string, ThemePalette> = {
  // Yellow theme (for companies like car services)
  yellow: {
    primary: {
      50: "#fefce8",
      100: "#fef9c3",
      200: "#fef08a",
      300: "#fde047",
      400: "#facc15",
      500: "#eab308",
      600: "#ca8a04",
      700: "#a16207",
      800: "#854d0e",
      900: "#713f12",
    },
    secondary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    accent: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e",
    },
    neutral: {
      50: "#fafafa",
      100: "#f4f4f5",
      200: "#e4e4e7",
      300: "#d4d4d8",
      400: "#a1a1aa",
      500: "#71717a",
      600: "#52525b",
      700: "#3f3f46",
      800: "#27272a",
      900: "#18181b",
      black: "#000000",
      white: "#ffffff",
    },
  },
  // Blue theme (for companies like barbershops)
  blue: {
    primary: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
    },
    secondary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    accent: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d",
    },
    neutral: {
      50: "#fafafa",
      100: "#f4f4f5",
      200: "#e4e4e7",
      300: "#d4d4d8",
      400: "#a1a1aa",
      500: "#71717a",
      600: "#52525b",
      700: "#3f3f46",
      800: "#27272a",
      900: "#18181b",
      black: "#000000",
      white: "#ffffff",
    },
  },
  // Purple theme (alternative)
  purple: {
    primary: {
      50: "#faf5ff",
      100: "#f3e8ff",
      200: "#e9d5ff",
      300: "#d8b4fe",
      400: "#c084fc",
      500: "#a855f7",
      600: "#9333ea",
      700: "#7c2d12",
      800: "#6b21a8",
      900: "#581c87",
    },
    secondary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    accent: {
      50: "#ecfdf5",
      100: "#d1fae5",
      200: "#a7f3d0",
      300: "#6ee7b7",
      400: "#34d399",
      500: "#10b981",
      600: "#059669",
      700: "#047857",
      800: "#065f46",
      900: "#064e3b",
    },
    neutral: {
      50: "#fafafa",
      100: "#f4f4f5",
      200: "#e4e4e7",
      300: "#d4d4d8",
      400: "#a1a1aa",
      500: "#71717a",
      600: "#52525b",
      700: "#3f3f46",
      800: "#27272a",
      900: "#18181b",
      black: "#000000",
      white: "#ffffff",
    },
  },
  // Black theme (sleek and professional)
  black: {
    primary: {
      50: "#f8f9fa",
      100: "#f1f3f4",
      200: "#e8eaed",
      300: "#dadce0",
      400: "#bdc1c6",
      500: "#9aa0a6",
      600: "#80868b",
      700: "#5f6368",
      800: "#3c4043",
      900: "#202124",
    },
    secondary: {
      50: "#f8f9fa",
      100: "#f1f3f4",
      200: "#e8eaed",
      300: "#dadce0",
      400: "#bdc1c6",
      500: "#9aa0a6",
      600: "#80868b",
      700: "#5f6368",
      800: "#3c4043",
      900: "#202124",
    },
    accent: {
      50: "#f3f4f6",
      100: "#e5e7eb",
      200: "#d1d5db",
      300: "#9ca3af",
      400: "#6b7280",
      500: "#4b5563",
      600: "#374151",
      700: "#1f2937",
      800: "#111827",
      900: "#030712",
    },
    neutral: {
      50: "#fafafa",
      100: "#f4f4f5",
      200: "#e4e4e7",
      300: "#d4d4d8",
      400: "#a1a1aa",
      500: "#71717a",
      600: "#52525b",
      700: "#3f3f46",
      800: "#27272a",
      900: "#18181b",
      black: "#000000",
      white: "#ffffff",
    },
  },
};

// Function to get theme based on company
export const getThemePalette = (company: Company | null): ThemePalette => {
  if (!company) {
    return themePalettes.blue; // Default theme
  }

  // Map company categories to themes
  switch (company.category) {
    case "car":
      return themePalettes.blue;
    case "barbershop":
      return themePalettes.black;
    default:
      return themePalettes.blue;
  }
};

// Function to get theme palette by name directly
export const getThemePaletteByName = (themeName: string): ThemePalette => {
  return themePalettes[themeName] || themePalettes.blue;
};

// Function to get available theme names
export const getAvailableThemes = (): string[] => {
  return Object.keys(themePalettes);
};

// Helper function to get theme class names
export const getThemeClasses = (company: Company | null) => {
  const theme = getThemePalette(company);

  return {
    // Primary colors
    primaryBg: `bg-[${theme.primary[500]}]`,
    primaryBgHover: `hover:bg-[${theme.primary[600]}]`,
    primaryText: `text-[${theme.primary[600]}]`,
    primaryBorder: `border-[${theme.primary[500]}]`,

    // Secondary colors
    secondaryBg: `bg-[${theme.secondary[100]}]`,
    secondaryText: `text-[${theme.secondary[600]}]`,

    // Accent colors
    accentBg: `bg-[${theme.accent[500]}]`,
    accentBgHover: `hover:bg-[${theme.accent[600]}]`,
    accentText: `text-[${theme.accent[600]}]`,

    // Gradients
    primaryGradient: `bg-gradient-to-r from-[${theme.primary[500]}] to-[${theme.primary[600]}]`,
    primaryGradientHover: `hover:from-[${theme.primary[600]}] hover:to-[${theme.primary[700]}]`,

    // Borders
    primaryBorderLight: `border-[${theme.primary[200]}]`,
    primaryBorderMedium: `border-[${theme.primary[400]}]`,

    // Backgrounds
    primaryLight: `bg-[${theme.primary[50]}]`,
    primaryLighter: `bg-[${theme.primary[100]}]`,
  };
};

// CSS custom properties for dynamic themes
export const generateThemeCSS = (company: Company | null): string => {
  const theme = getThemePalette(company);

  return `
    :root {
      --color-primary-50: ${theme.primary[50]};
      --color-primary-100: ${theme.primary[100]};
      --color-primary-200: ${theme.primary[200]};
      --color-primary-300: ${theme.primary[300]};
      --color-primary-400: ${theme.primary[400]};
      --color-primary-500: ${theme.primary[500]};
      --color-primary-600: ${theme.primary[600]};
      --color-primary-700: ${theme.primary[700]};
      --color-primary-800: ${theme.primary[800]};
      --color-primary-900: ${theme.primary[900]};
      
      --color-secondary-50: ${theme.secondary[50]};
      --color-secondary-100: ${theme.secondary[100]};
      --color-secondary-200: ${theme.secondary[200]};
      --color-secondary-300: ${theme.secondary[300]};
      --color-secondary-400: ${theme.secondary[400]};
      --color-secondary-500: ${theme.secondary[500]};
      --color-secondary-600: ${theme.secondary[600]};
      --color-secondary-700: ${theme.secondary[700]};
      --color-secondary-800: ${theme.secondary[800]};
      --color-secondary-900: ${theme.secondary[900]};
      
      --color-accent-50: ${theme.accent[50]};
      --color-accent-100: ${theme.accent[100]};
      --color-accent-200: ${theme.accent[200]};
      --color-accent-300: ${theme.accent[300]};
      --color-accent-400: ${theme.accent[400]};
      --color-accent-500: ${theme.accent[500]};
      --color-accent-600: ${theme.accent[600]};
      --color-accent-700: ${theme.accent[700]};
      --color-accent-800: ${theme.accent[800]};
      --color-accent-900: ${theme.accent[900]};
      
      --color-neutral-50: ${theme.neutral[50]};
      --color-neutral-100: ${theme.neutral[100]};
      --color-neutral-200: ${theme.neutral[200]};
      --color-neutral-300: ${theme.neutral[300]};
      --color-neutral-400: ${theme.neutral[400]};
      --color-neutral-500: ${theme.neutral[500]};
      --color-neutral-600: ${theme.neutral[600]};
      --color-neutral-700: ${theme.neutral[700]};
      --color-neutral-800: ${theme.neutral[800]};
      --color-neutral-900: ${theme.neutral[900]};
      --color-neutral-black: ${theme.neutral.black};
      --color-neutral-white: ${theme.neutral.white};
    }
  `;
};

export default getThemePalette;
